import { TBasicInputInstance } from 'widgets/forms/BasicInput';
import { TProductVariantSelect } from './ProductVariantSelect';

/**
 * @description implementation of product options for ProductVariantSelect widget
 * @param ProductVariantSelect Base widget for extending
 * @returns Product Option VariantSelect widget
 */
function ProductOptionSelectClassCreator(ProductVariantSelect: TProductVariantSelect) {
    /**
     * @category widgets
     * @subcategory product
     * @class ProductOptionSelect
     * @augments ProductVariantSelect
     *
     * @example <caption>Product Option VariantSelect widget usage</caption>
     * <fieldset
     *     class="b-product_option"
     *     data-widget="productOptionSelect"
     *     data-widget-event-change="changeAttribute"
     *     data-forward-to-parent="handleCustomOptionError"
     *     data-id="option-${option.id}"
     *     data-skip-validation="true"
     * >
     *     <div data-ref="container">
     *         <legend class="b-product_option-heading" id="group-label-${option.id}">
     *             <span class="b-product_option-name">${option.name}</span>
     *             <isif condition="${option.description}">
     *                 <span class="b-product_option-description">${option.description}</span>
     *             </isif>
     *         </legend>
     *         <div class="b-product_option-content" role="radiogroup" aria-labelledby="group-label-${option.id}">
     *             <isloop items="${option.values}" var="value">
     *                 <label class="b-product_option-item b-radio">
     *                     <input
     *                         class="b-radio-input"
     *                         type="radio"
     *                         name="${option.id}"
     *                         data-event-click.prevent="selectVariation"
     *                         data-attr-url="${value.url}"
     *                         data-attr-is-selected="${value.selected}"
     *                         value="${value.url}"
     *                         data-value-id="${value.id}"
     *                         data-is-custom="${value.isCustomOption}"
     *                         ${value.selected ? 'checked' : ''}
     *                     />
     *                     <span class="b-radio-icon"></span>
     *                     <span class="b-radio-label">
     *                         <span class="b-product_option-item_name">${value.displayValue}</span> - ${value.isFree ? Resource.msg('price.free', 'product', null) : value.price }
     *                     </span>
     *                 </label>
     *             </isloop>
     *         </div>
     *     </div>
     *     <script type="template/mustache" data-ref="template">
     *         <div data-ref="container">
     *             ... dynamic data of product option
     *         </div>
     *     </script>
     * </fieldset>
     */
    class ProductOptionSelect extends ProductVariantSelect {
        /**
         * @description Get input value
         * @param id - data-id
         * @returns input value
         */
        getInputValueByID(id: string): string {
            let value = '';

            this.getById(id, (textArea: TBasicInputInstance) => {
                value = textArea.getValue();
            });

            return value;
        }

        /**
         * @description Validate input and return its validation result
         * @param id  data-id
         * @returns Input validation result
         */
        validateInputByID(id: string): boolean {
            let isValid = false;

            this.getById(id, (textArea: TBasicInputInstance) => {
                isValid = textArea.validate();
            });

            return isValid;
        }
    }

    return ProductOptionSelect;
}

export type TProductOptionSelect = ReturnType<typeof ProductOptionSelectClassCreator>;

export type TProductOptionSelectInstance = InstanceType<TProductOptionSelect>;

export default ProductOptionSelectClassCreator;
